import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import discussQuote from 'src/api/quote/discuss-quote';
import Page from 'src/components/Page';
import { getQuoteThunk, selectQuote } from 'src/redux/slices/quoteSlice';
import { dispatch } from 'src/redux/store';
import { Box } from '@mui/system';
import Progress from 'src/components/Progress';
import { Accordion, Button, Typography } from '@mui/material';
import { openModal } from 'src/redux/slices/modal';
import modals from 'src/constants/modals';
import { AuthPage } from '../auth/styles';
import { QuoteSection } from '../Quote/styled';
import ReportMissingQuoteAddressModal from '../Quote/components/ReportMissingQuoteAddressModal';
import QuoteQuestionsModal from './components/QuoteQuestionsModal';
import QuoteMeetingModal from './components/QuoteMeetingModal';
import QuoteCallbackModal from './components/QuoteCallbackModal';
import QuoteMessageModal from './components/QuoteMessageModal';
import { AccordionSummary, AccordionDetails } from './styled';

const DiscussQuote = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const quote = useSelector(selectQuote);

	const quoteId = searchParams.get('id');
	const token = searchParams.get('token');

	useEffect(() => {
		const setup = async () => {
			try {
				console.log('quoteId', quoteId);
				console.log('token', token);

				if (!quoteId || !token) {
					navigate('/auth/login', { replace: true });

					return;
				}

				await discussQuote({ id: quoteId, token });

				dispatch(getQuoteThunk({ id: quoteId, withLoading: true }));
			} catch (e) {
				console.log('e', e);

				navigate('/auth/login', { replace: true });
			}
		};

		setup();
	}, [quoteId, token]);

	const handleLogin = () => navigate(`/auth/login`);
	const handleReady = () => {
		sessionStorage.setItem('quoteId', quoteId as string);

		navigate(`/quote/${quoteId}`);
	};

	const handleHaveQuestions = () => {
		dispatch(openModal({ name: modals.quoteQuestions }));
	};

	return (
		<Page title='Quote'>
			<AuthPage sx={{ alignItems: 'flex-start', py: '100px', overflowY: 'scroll' }}>
				<Box display='flex' alignItems='center' flexDirection='column'>
					{!quote.isInitialized ? (
						<Progress />
					) : quote.error || !quote.data ? (
						<Box display='flex' flexDirection='column' gap='10px' alignItems='center'>
							<Typography color='red' fontSize='16px' fontWeight={600}>
								{quote.error?.message ?? 'Something went wrong'}
							</Typography>
							<Button onClick={handleLogin} variant='contained'>
								Go to Login
							</Button>
						</Box>
					) : (
						<Box display='flex' flexDirection='column' gap='15px' maxWidth='750px' minWidth='750px'>
							<QuoteSection style={{ padding: '20px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
								<Typography variant='h3' style={{ fontWeight: 'bold', marginBottom: '10px', color: '#333' }}>
									FAQs
								</Typography>
								<Typography variant='body1' style={{ fontSize: '17px', color: '#555', lineHeight: '1.6' }}>
									Here’s a list of questions we often get asked. Reading through this might save you time!
								</Typography>
							</QuoteSection>
							<QuoteSection sx={{ py: '20px' }}>
								<Accordion>
									<AccordionSummary>Why choose Conveyo?</AccordionSummary>
									<AccordionDetails style={{ textAlign: 'justify' }}>
										At Conveyo, we’re transforming property transactions – whether you’re buying or selling, we make the
										process faster, simpler, and stress-free.
										<br />
										<br />
										<div style={{ textAlign: 'left' }}>
											<ul>
												<li>
													<b>Transparent Pricing</b> – No hidden costs, just clear, fixed fees.
												</li>
												<li>
													<b>No Move, No Fee</b> – If your transaction falls through, you don't pay legal fees.
												</li>
												<li>
													<b>Highly Rated Service</b> – Trusted by thousands, with 5* reviews on Trustpilot & Google.
												</li>
												<li>
													<b>Only the Best Professionals</b> – We work with vetted experts to support your journey.
												</li>
												<li>
													<b>Designed Around You</b> – A modern, client-friendly experience that saves you time.
												</li>
											</ul>
										</div>
									</AccordionDetails>
								</Accordion>
								<Accordion>
									<AccordionSummary>What does Conveyo do?</AccordionSummary>
									<AccordionDetails style={{ textAlign: 'justify' }}>
										When you're <b>buying or selling a property</b>, there’s a lot to sort out: solicitors,
										certificates, banking checks, and more. Instead of leaving you to figure it all out,{' '}
										<b>Conveyo handles everything for you.</b>
										<br />
										<br />
										We provide:
										<br />
										<br />
										<div style={{ textAlign: 'left' }}>
											<>
												✅ <b>A Trusted Solicitor</b> – We connect you with a vetted legal expert who handles all the
												conveyancing aspects of your transaction.
											</>
											<br />
											<>
												✅ <b>All Evidence You Need</b> – We arrange your AML (anti-money laundering) checks, ID
												verification, source of fund checks, banking checks, and property searches — ensuring everything
												is in place for a smooth transaction.
											</>
											<br />
											<>
												✅ <b>A Managed Process</b> – We coordinate between you, your estate agent, your solicitor, and
												your counterparty, making sure everyone is aligned, documents are in order, and things move
												forward without unnecessary delays.
											</>
											<br />
											<>
												✅ <b>Support Every Step of the Way</b> – Got questions? Need help? We’re here to guide you.
											</>
											<br />
											<>
												✅ <b>Additional Services</b> – From surveys to estate planning and anything else required, we
												offer the services you need to make your move even easier.
											</>
											<br />
											<br />
											<b>Think of us as your personal transaction concierge</b> — we arrange everything and keep things
											moving, so you can focus on living your life.
										</div>
									</AccordionDetails>
								</Accordion>
								<Accordion>
									<AccordionSummary>What’s included in my quote?</AccordionSummary>
									<AccordionDetails style={{ textAlign: 'justify' }}>
										Your <b>quote</b> includes all the essential services and standard disbursements required for a
										property transaction, such as:
										<br />
										<br />
										<ul>
											<li>
												<b>Legal Fees</b> – Covering the legal work provided by your solicitor.
											</li>
											<li>
												<b>ID Checks</b> – Anti-money laundering (AML) checks and identity verification.
											</li>
											<li>
												<b>Official Copies</b> – Key documents like the title deeds.
											</li>
											<li>
												<b>Gift Declarations</b> – If applicable, related to any gifts used in the purchase.
											</li>
											<li>
												<b>Searches</b> – If applicable, including property, local authority, and environmental.
											</li>
										</ul>
										<br />
										These are all considered <b>standard disbursements</b> that are included in your quote, so there are
										no hidden costs. The goal is to provide full transparency and ensure everything is covered for you.
									</AccordionDetails>
								</Accordion>
								<Accordion>
									<AccordionSummary>Do I need to put money on account?</AccordionSummary>
									<AccordionDetails style={{ textAlign: 'justify' }}>
										Your solicitor may ask you to put a <b>small amount</b> (typically <b>£50-£100</b>) on account at
										instruction. This amount is already included in your quote, so you don’t need to worry about it as
										an additional cost.
										<br />
										<br />
										If you're selling a <b>leasehold</b> or a <b>managed freehold</b>, you’ll need to pay for the{' '}
										<b>management pack </b>
										directly to your management company. Prices for this can vary, typically ranging from
										<b> £100 to £500.</b>
									</AccordionDetails>
								</Accordion>
								<Accordion>
									<AccordionSummary>When is payment due?</AccordionSummary>
									<AccordionDetails style={{ textAlign: 'justify' }}>
										The <b>total amount quoted</b> by Conveyo is invoiced in <b>two parts:</b>
										<br />
										<br />
										<ol>
											<li>
												<b>At Exchange</b> – Conveyo issues an invoice for the <b>first part</b> of the fees. This
												covers our service, including managing the process, handling data and documents, reviewing them,
												and coordinating between all parties involved in your transaction.
											</li>
											<br />
											<li>
												<b>At Completion</b> – The <b>second part</b> of the fee is invoiced directly by your{' '}
												<b>solicitor. </b>
												This covers their legal work, provided at the <b>special rate</b> we’ve negotiated for you.
											</li>
										</ol>
										<br />
										Together, these two amounts make up the <b>total quote</b> you receive from Conveyo.
									</AccordionDetails>
								</Accordion>
								<Accordion>
									<AccordionSummary>Why are you asking me to pay extra on top of the solicitor’s fee?</AccordionSummary>
									<AccordionDetails style={{ textAlign: 'justify' }}>
										The <b>Conveyo fee</b> is <b>not an add-on</b> to the solicitor’s fee. The <b>total quote</b> you
										receive from Conveyo is the <b>same</b> amount you would typically pay for conveyancing at the
										average market rate. We’ve negotiated a <b>better rate</b> for your solicitor’s services, and on top
										of that, we coordinate and streamline the entire process, offering a level of service that a typical
										solicitor wouldn’t provide.
										<br />
										<br />
										Our goal is to ensure that our innovation comes at <b>no extra cost</b> to you. So, while you may
										wonder if you could simply pay the solicitor’s fee alone, <b>Conveyo offers much more</b>—all for
										the same price you'd expect to pay for traditional conveyancing.
									</AccordionDetails>
								</Accordion>
								<Accordion>
									<AccordionSummary>I'm selling – why are you asking me to order searches?</AccordionSummary>
									<AccordionDetails style={{ textAlign: 'justify' }}>
										We strongly recommend that all sellers order the searches at instruction. Here's why:
										<br />
										<br />
										<div style={{ textAlign: 'left' }}>
											<ul>
												<li>
													<b>Time-Saving</b> – The searches last for a year and will be passed on to your buyer, saving
													up to <b>8 weeks</b> in the process.
												</li>
												<li>
													<b>Boosting Confidence</b> – Having these searches ready <b>in advance</b> shows your buyer
													that you’re prepared and serious about selling.
												</li>
												<li>
													<b>Guaranteed Acceptance</b> – The searches we recommend are exactly the same as those your
													buyers’ solicitor would obtain. Hence, the vast majority of solicitors accept our searches -
													and if your buyers use <b>Conveyo,</b> there’s a <b>100% guarantee</b> they’ll accept the
													searches.
												</li>
												<li>
													<b>No Extra Cost</b> – The searches are a buyer cost and you'll receive a refund when they pay
													for the searches.
												</li>
											</ul>
										</div>
										<br />
										Ordering searches upfront gives you a competitive edge and ensures a smoother sale.
									</AccordionDetails>
								</Accordion>
								<Accordion>
									<AccordionSummary>How quickly can I expect to complete?</AccordionSummary>
									<AccordionDetails style={{ textAlign: 'justify' }}>
										With <b>Conveyo,</b> the process can take just <b>30 days</b> from sale agreed to exchange, compared
										to a UK national average of <b>22 weeks</b>.
										<br />
										<br />
										While we can’t control every aspect, such as the performance of chain solicitors we don’t work with
										or changes in personal circumstances, with Conveyo you’re <b>maximising your chances</b> of a fast,
										orderly transaction. We proactively manage all steps and minimise delays, so you can be confident
										that you’re on the <b>fastest track</b> possible.
									</AccordionDetails>
								</Accordion>
								<Accordion>
									<AccordionSummary>Are you solicitors? I can’t find you on the SRA website.</AccordionSummary>
									<AccordionDetails style={{ textAlign: 'justify' }}>
										No, <b>Conveyo is not a law firm</b>, which is why you won’t find us on the Solicitors Regulation
										Authority (SRA) website. We are a <b>technology company</b> that specialises in legal process
										automation, designed to make conveyancing faster and more efficient.
										<br />
										<br />
										We empower solicitors by handling the administrative burden, streamlining communication, and
										providing property data—allowing them to focus on the legal work.{' '}
										<b>Your solicitor remains the legal authority</b> on your case and is fully regulated by the SRA or
										the Council of Licensed Conveyancers (CLC).
										<br />
										<br />
										With Conveyo, you get the <b>best of both worlds</b> — a modern, technology-driven experience
										alongside expert legal oversight.
									</AccordionDetails>
								</Accordion>
								<Accordion>
									<AccordionSummary>Why are other conveyancing quotes for my property much cheaper?</AccordionSummary>
									<AccordionDetails style={{ textAlign: 'justify' }}>
										Conveyancing quotes for the average-priced UK property can range anywhere from <b>£500 to £2,000</b>{' '}
										— which is a <b>huge difference</b> for what should be the same legal process. So why the big gap?
										<br />
										<br />
										At the <b>lower end</b>, ultra-cheap conveyancing firms cut costs aggressively. Much of the work is
										farmed out to overseas workers who have no real conveyancing experience. The solicitor handling your
										case will only glance at it at the last minute, juggling dozens or even hundreds of other
										transactions.
										<br />
										<br />
										At the <b>higher end</b>, boutique firms often overcharge, adding made-up fees simply because they
										can. While they might appear to offer a more personalised experience, conveyancing is generally a{' '}
										<b>straightforward legal process</b> — it doesn’t need to cost a fortune.
										<br />
										<br />
										With Conveyo, you get the <b>the perfect balance</b> — a <b>fair price</b>, <b>expert solicitors</b>
										, and a <b>streamlined process</b> that eliminates delays and inefficiencies. We make conveyancing{' '}
										<b>simple</b>, <b>transparent</b>, and <b>fast</b> — just as it should be.
									</AccordionDetails>
								</Accordion>
							</QuoteSection>
							<Box display='flex' gap='15px' alignItems='center' justifyContent='center'>
								<Button
									onClick={handleReady}
									variant='contained'
									sx={{
										mt: '15px',
										padding: '12px 24px', // Chunky button
										fontSize: '18px',
										textTransform: 'none', // Enforce sentence case
									}}
								>
									I'm ready!
								</Button>

								<Button
									onClick={handleHaveQuestions}
									variant='outlined'
									sx={{
										mt: '15px',
										padding: '12px 24px', // Chunky button
										fontSize: '18px',
										textTransform: 'none', // Enforce sentence case
										backgroundColor: 'white',
									}}
								>
									I still have questions
								</Button>
							</Box>
						</Box>
					)}
				</Box>
			</AuthPage>
			<ReportMissingQuoteAddressModal />
			<QuoteQuestionsModal />
			<QuoteMeetingModal />
			<QuoteCallbackModal />
			<QuoteMessageModal />
		</Page>
	);
};

export default DiscussQuote;
